import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Download, Heart, Sparkles, Headphones } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import StickyCTA from "@/components/StickyCTA";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const STARTER_KIT_VIDEO =
  "https://customer-assets.emergentagent.com/job_pip-revamp/artifacts/u79uzcso_A801FA0A-6349-45CF-980C-7BFE863DDC1C.mp4";

const SECTIONS = [
  {
    eyebrow: "Before You Begin",
    title: "A soft return to the self who's been waiting.",
    body: [
      "This isn't a worksheet. It's a small, loving pause — a few honest pages that invite you back to the one voice that's been gently trying to get your attention underneath everything else.",
      "You don't have to fix anything here. You don't have to get it right. You just have to stay long enough to notice what's real.",
      "The noise — the overthinking, the \"shoulds,\" the inner critic who's been doing overtime — isn't proof that something's wrong with you. It's proof that part of you has been working very, very hard to keep you safe. This little kit is a soft thank-you to that part… and a gentle you can rest now.",
    ],
  },
  {
    eyebrow: "Why We Start in the Quiet",
    title: "Your brain doesn't speak in logic. It speaks in patterns.",
    body: [
      "Whatever you've practised most — overthinking, bracing, self-doubt, hurry — your system recognises as familiar. And familiar feels safe… even when it doesn't feel good.",
      "That's why \"trying harder\" to be calm rarely works. Calm isn't something you force. It's something your nervous system has to be gently, repeatedly shown is safe. That's what this kit is for.",
      "We're not chasing a new version of you here. We're returning to the version of you that's been there the whole time — quieter than the noise, steadier than the stories, waiting patiently for you to listen.",
    ],
  },
  {
    eyebrow: "The Three-Breath Arrival",
    title: "A tiny ritual for any moment the noise gets loud.",
    body: [
      "Breath one — in through the nose, long exhale through the mouth: This is my arrival.",
      "Breath two — in through the nose, exhale and let your shoulders drop: This is my permission to be here.",
      "Breath three — in through the nose, exhale and whisper: I'm here. I've got me.",
      "Fifteen seconds. Come back to it any time.",
    ],
  },
  {
    eyebrow: "The Pebbles in Your Shoe",
    title: "Your patterns aren't problems. They're brilliant.",
    body: [
      "I often think of the noise like pebbles in your shoe. Not big enough to stop you moving forward. But enough to make every step feel a little uncomfortable. Enough to leave you tired in ways you can't quite explain.",
      "Maybe your pebble is the way you say yes when you mean no. Maybe it's the pressure to prove yourself. Maybe it's the voice that narrates every small mistake for three days longer than it needs to.",
      "You don't have to remove them all. You only have to remove one. Because the moment you do, everything else feels lighter.",
    ],
  },
  {
    eyebrow: "The Kindest Question",
    title: "What were you trying to do for me?",
    body: [
      "Name one pebble you've been walking on. Then — with loving curiosity, the kind you'd offer your dearest friend — ask the question. Let the answer land however it lands.",
      "Your patterns aren't broken. They kept you safe once. You've just outgrown them — and that's a good thing.",
    ],
  },
  {
    eyebrow: "One Last Thing",
    title: "You're not behind.",
    body: [
      "You're not too late. You're not broken. You're someone who just picked up a small, quiet kit on a random day and chose to pay attention to yourself.",
      "That choice matters far more than you realise. Keep breathing. Keep noticing. Keep letting love lead.",
      "I'm so proud of you.",
    ],
  },
];

function OptInForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [audioUrl, setAudioUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/lead-magnet", { name, email });
      setDownloadUrl(data?.download_url || "/api/starter-kit/download");
      setAudioUrl(data?.audio_url || "/api/audio/quiet-the-noise-meditation.m4a");
      setDone(true);
      toast.success("Your Starter Kit is ready below.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="bg-sage/5 border border-sage/20 rounded-2xl p-8 text-center" data-testid="starterkit-success">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-4">
          <Check size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-2xl text-ink mb-2">Your Starter Kit is here.</h3>
        <p className="text-ink-soft mb-6">Save these links — you'll also receive an email shortly.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-3 font-medium text-sm transition-all"
            data-testid="starterkit-download-pdf"
          >
            Download PDF
          </a>
          <a
            href={audioUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-5 py-3 font-medium text-sm transition-all"
            data-testid="starterkit-listen-audio"
          >
            Listen to meditation
          </a>
        </div>
        <div className="max-w-md mx-auto mt-6 bg-cream-soft border border-hairline rounded-2xl p-5 text-left">
          <div className="eyebrow mb-3 text-sage text-center">A small note from Pip</div>
          <video
            src={STARTER_KIT_VIDEO}
            controls
            playsInline
            preload="metadata"
            className="w-full rounded-xl bg-ink"
            data-testid="starterkit-success-video"
          />
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="starterkit-form" autoComplete="off">
      <input
        required
        type="text"
        name="first_name"
        autoComplete="given-name"
        placeholder="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="md:col-span-2 bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
        data-testid="starterkit-name"
      />
      <input
        required
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="md:col-span-2 bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
        data-testid="starterkit-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-4 font-medium tracking-wide transition-all disabled:opacity-50"
        data-testid="starterkit-submit"
      >
        {loading ? "Sending…" : "Yes — I want this"}
      </button>
    </form>
  );
}

export default function StarterKit() {
  useSeo({
    title: "Quiet The Noise — Free Starter Kit · Pip Bennett",
    description: "A short PDF workbook + 10-minute guided meditation to soften the inner critic and find your first real moments of peace. Free, instant download.",
    canonical: "https://pipbennett.com/starter-kit",
  });
  return (
    <div data-testid="page-starter-kit">
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 md:pt-20 pb-16 md:pb-20">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 100%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-sage">Free Starter Kit · Workbook + Guided Meditation</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.02]">
              Quiet The <em className="italic text-sage">Noise</em>.
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-ink-soft leading-relaxed">
              A guided meditation and strategic life coaching questions to help you recognise your noise,
              get curious about it, and step away. Your first real moment of peace — free.
            </p>
            <div id="get-the-kit" className="mt-10 max-w-2xl mx-auto scroll-mt-24">
              <OptInForm />
            </div>
            <p className="mt-4 text-xs text-ink-soft/70 flex items-center justify-center gap-1.5">
              <Download size={12} strokeWidth={1.5} />
              PDF + audio meditation delivered to your inbox. No spam. Unsubscribe anytime.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* READABLE CONTENT (SEO + dwell) */}
      <section className="bg-cream-soft py-20 md:py-28 border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="text-center mb-16">
              <div className="eyebrow mb-4 text-sage">Preview the Workbook</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
                Read it here, or have the full PDF sent to your inbox.
              </h2>
            </div>
          </SectionReveal>

          {/* Guided meditation player */}
          <SectionReveal>
            <div className="bg-sage text-cream rounded-2xl p-8 md:p-12 mb-16 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gold/20 blur-3xl" />
              <div className="flex items-start gap-4 mb-6 relative">
                <div className="w-12 h-12 rounded-full bg-gold/20 inline-flex items-center justify-center flex-shrink-0">
                  <Headphones size={20} strokeWidth={1.4} className="text-gold" />
                </div>
                <div>
                  <div className="eyebrow mb-1 text-gold">Guided Meditation</div>
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">
                    Quiet The Noise — a 10-minute soft return.
                  </h3>
                </div>
              </div>
              <p className="text-cream/80 leading-relaxed mb-6 max-w-xl relative">
                Press play whenever you have a quiet moment — even right now. No music. Just a gentle invitation home to yourself. The full PDF workbook is waiting in your inbox.
              </p>
              <audio
                controls
                src={`${process.env.REACT_APP_BACKEND_URL}/api/audio/quiet-the-noise-meditation.m4a`}
                className="w-full relative"
                data-testid="starterkit-audio-player"
              >
                Your browser doesn't support audio playback.
              </audio>
            </div>
          </SectionReveal>

          <div className="space-y-16">
            {SECTIONS.map((s, i) => (
              <SectionReveal key={i} delay={i * 60}>
                <article className="bg-white border border-hairline rounded-2xl p-8 md:p-12" data-testid={`starterkit-section-${i}`}>
                  <div className="eyebrow text-gold-dark mb-3">{s.eyebrow}</div>
                  <h3 className="font-serif-display text-3xl md:text-[2.2rem] text-ink leading-tight mb-6">
                    {s.title}
                  </h3>
                  <div className="space-y-5 text-ink leading-relaxed text-[1.02rem]">
                    {s.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </article>
              </SectionReveal>
            ))}
          </div>

          <SectionReveal>
            <div className="mt-20 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sage text-cream mb-6">
                <Heart size={20} strokeWidth={1.5} />
              </div>
              <p className="font-serif-display text-2xl md:text-3xl text-sage italic mb-4">
                Love, Pip xox
              </p>
              <p className="text-ink-soft max-w-md mx-auto">
                Want the full workbook — all reflections, rituals and the 10-minute guided meditation?
              </p>
              <a
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="mt-8 inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                data-testid="starterkit-scroll-form"
              >
                Send me the full kit
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* BRIDGE TO TMI */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="inline-flex items-center gap-2 bg-gold/15 text-gold-dark text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 rounded-full mb-8">
              <Sparkles size={12} strokeWidth={2} /> If this landed…
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
              There's a deeper conversation waiting for you.
            </h2>
            <p className="mt-8 max-w-2xl mx-auto text-ink-soft leading-relaxed text-lg">
              <em>The Menopause Invitation</em> is my newest digital program — six grounded coaching modules with
              an interactive workbook that auto-saves your reflections. A different way to meet menopause: with
              awareness, not anxiety.
            </p>
            <Link
              to="/the-menopause-invitation"
              className="mt-10 inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
              data-testid="starterkit-tmi-cta"
            >
              Explore The Menopause Invitation — $87
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </SectionReveal>
        </div>
      </section>
      <StickyCTA
        label="Get the Kit"
        href="#get-the-kit"
        testId="sticky-cta-starter-kit"
      />
    </div>
  );
}
